import * as React from "react";
import { Box, Button, Paper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export type MobileActionBarMode = "create" | "edit" | "view";

export interface MobileActionBarProps {
  mode: MobileActionBarMode;
  isReviewing: boolean;
  primaryDisabled?: boolean;
  showPrint?: boolean;
  printDisabled?: boolean;
  onPrint?: () => void;
  onReview?: () => void;
  onExitReview?: () => void;
  onSubmit?: () => void;
  onSaveChanges?: () => void;
}

const secondaryBtn: React.CSSProperties = {
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  background: "transparent",
  color: "#0a2540",
  border: "2px solid #0a2540",
  borderRadius: 6,
  minWidth: 80,
  whiteSpace: "nowrap",
};

const iconBtn: React.CSSProperties = {
  padding: "8px 12px",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  background: "transparent",
  color: "#0a2540",
  border: "2px solid #0a2540",
  borderRadius: 6,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export function MobileActionBar({
  mode,
  isReviewing,
  primaryDisabled = false,
  showPrint = true,
  printDisabled = false,
  onPrint,
  onReview,
  onExitReview,
  onSubmit,
  onSaveChanges,
}: MobileActionBarProps) {
  const wrapperSx = {
    borderTop: "1px solid",
    borderColor: "divider",
    px: 1,
    py: 1,
    display: "flex",
    gap: 1,
    alignItems: "center",
  };

  // View mode: Print only
  if (mode === "view") {
    return (
      <Paper elevation={0} sx={{ borderTop: "1px solid", borderColor: "divider", p: 1 }}>
        <Button fullWidth variant="contained" onClick={onPrint} disabled={printDisabled}>
          Print PDF
        </Button>
      </Paper>
    );
  }

  // Reviewing: ← back + primary action
  if (isReviewing) {
    const primaryLabel = mode === "create" ? "Submit" : "Save Changes";
    const primaryHandler = mode === "create" ? onSubmit : onSaveChanges;
    return (
      <Paper elevation={0} sx={wrapperSx}>
        <button onClick={onExitReview} style={iconBtn} aria-label="back to editing">
          <ArrowBackIcon style={{ fontSize: 20 }} />
        </button>
        <Box sx={{ flex: 1 }}>
          <Button fullWidth variant="contained" onClick={primaryHandler} disabled={primaryDisabled}>
            {primaryLabel}
          </Button>
        </Box>
      </Paper>
    );
  }

  // Editing: Print + Review
  return (
    <Paper elevation={0} sx={wrapperSx}>
      {showPrint && (
        <button
          onClick={onPrint}
          disabled={printDisabled}
          style={{
            ...secondaryBtn,
            opacity: printDisabled ? 0.4 : 1,
            cursor: printDisabled ? "not-allowed" : "pointer",
          }}
        >
          Print
        </button>
      )}
      <Box sx={{ flex: 1 }}>
        <Button fullWidth variant="contained" onClick={onReview} disabled={primaryDisabled}>
          Review
        </Button>
      </Box>
    </Paper>
  );
}